import React, { useState, useEffect, useContext } from 'react';
import { Table, Card, Typography, Input, message } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { LoginContext } from '../../context/loginContext';

const { Title } = Typography;

const CPosicionesArticulo = () => {
  const { user } = useContext(LoginContext);
  const [clientes, setClientes] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  useEffect(() => {
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCliente) {
      fetchArticulosCliente(selectedCliente._id);
    } else {
      setArticulos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCliente]);

  const fetchClientes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/usuarios/clientes`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setClientes(response.data);
    } catch {
      message.error('Error al cargar la lista de clientes');
    }
  };

  const fetchArticulosCliente = async (clienteId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/movimientos/articulos/por-cliente/${clienteId}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      // response.data viene con:
      // [{ articulo: {...}, cantidad, posiciones: [{ posicion, cantidad, lotes: [{lote, fechaVencimiento, cantidad}]}]}]
      const articulosConPosiciones = response.data.map((item) => ({
        ...item,
        posiciones: item.posiciones || [],
        nombreArticulo: item.articulo?.nombre || '',
        marca: item.articulo?.idmarca?.nombre || '',
        categoria: item.articulo?.idcategoria?.nombre || '',
        unidadDeMedida: item.articulo?.unidadDeMedida || '',
        cantidadDisponible: item.cantidad || 0,
      }));

      articulosConPosiciones.sort((a, b) => b.cantidadDisponible - a.cantidadDisponible);
      setArticulos(articulosConPosiciones);
    } catch (e) {
      console.error(e);
      message.error('Error al cargar los artículos del cliente');
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button type="button" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)} style={{ marginRight: 8 }}>
            Buscar
          </button>
          <button type="button" onClick={() => handleReset(clearFilters)}>
            Resetear
          </button>
        </div>
      </div>
    ),
    filterIcon: (filtered) => (
      <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span style={{ backgroundColor: '#ffc069', padding: '0 5px' }}>{text}</span>
      ) : (
        text
      ),
  });

  // Columnas para la tabla de clientes con filtros individuales
  const clienteColumns = [
    {
      title: 'Usuario',
      dataIndex: 'usuario',
      key: 'usuario',
      ...getColumnSearchProps('usuario'),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ...getColumnSearchProps('email'),
    },
    {
      title: 'CUIT',
      dataIndex: 'cuit',
      key: 'cuit',
      ...getColumnSearchProps('cuit'),
    },
  ];

  const clienteRowSelection = {
    type: 'radio',
    onChange: (_, selectedRows) => {
      setSelectedCliente(selectedRows[0]);
    },
  };

  // Renderiza el detalle de posiciones con desagregado de lotes y vencimientos
  const renderPosicionesConLotes = (posiciones = []) => (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {posiciones.map((pos, i) => (
        <li key={`${pos.posicion}-${i}`}>
          <strong>{pos.posicion}</strong>: {Number(pos.cantidad).toFixed(2)} u
          {Array.isArray(pos.lotes) && pos.lotes.length > 0 && (
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
              {pos.lotes.map((l, j) => (
                <li key={`${pos.posicion}-lote-${j}`}>
                  Lote: {l.lote || '—'} · Venc.: {l.fechaVencimiento ? dayjs(l.fechaVencimiento).format('YYYY-MM-DD') : '—'} · Cant.: {Number(l.cantidad).toFixed(2)}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );

  // Columnas para la tabla de artículos
  const articuloColumns = [
    { title: 'Nombre Artículo', dataIndex: 'nombreArticulo', key: 'nombreArticulo' },
    { title: 'Marca', dataIndex: 'marca', key: 'marca' },
    { title: 'Categoría', dataIndex: 'categoria', key: 'categoria' },
    { title: 'Unidad de Medida', dataIndex: 'unidadDeMedida', key: 'unidadDeMedida' },
    {
      title: 'Posiciones (con lotes)',
      dataIndex: 'posiciones',
      key: 'posiciones',
      render: renderPosicionesConLotes,
    },
    { title: 'Cantidad Total', dataIndex: 'cantidadDisponible', key: 'cantidadDisponible', render: (cantidad) => Number(cantidad).toFixed(2) },
  ];

  return (
    <div>
      <Title level={4}>Seleccionar Cliente</Title>
      <Table
        rowSelection={clienteRowSelection}
        columns={clienteColumns}
        dataSource={clientes}
        rowKey="_id"
        pagination={{ pageSize: 5 }}
        style={{ marginBottom: '20px' }}
      />
      {selectedCliente && (
        <>
          <Title level={4}>Artículos del Cliente: {selectedCliente.usuario}</Title>
          <Table
            columns={articuloColumns}
            dataSource={articulos}
            rowKey={(r) => r?.articulo?._id || r?.nombreArticulo}
            pagination={{
              pageSize: 10,
              total: articulos.length,
              showTotal: (total) => `Total ${total} artículos`,
            }}
          />
        </>
      )}
    </div>
  );
};

export default CPosicionesArticulo;